import { useMemo, useState, useCallback, useEffect } from "react";

import debounce from "lodash.debounce";
import { getZcLoanCollateral, getZcLedger, getPandoraErrorMessage } from "@bridgesplit/abf-react";
import { lamportsToUiAmount } from "@bridgesplit/utils";
import { LoopWindType } from "@bridgesplit/abf-sdk";

import { MakeRepaymentProps } from "./types";
import { createRepaymentParams, useRepay } from "./util";
import { useLoanMarketHoursAlertConfig } from "../../util";
import { useManageLeverageContext } from "./ManageLeverageContext";
import RepaymentComponent from "./RepaymentComponent";

export default function CollateralRepayment({ loanExpanded }: MakeRepaymentProps) {
    const {
        setForm,
        form,
        repayWithCollateralQuoteError,
        repayWithCollateralQuote,
        fullRepayWithCollateralQuoteLoading,
        repayWithCollateralQuoteLoading,
        fullRepayAmountInCollateral,
        customRepayAmountInPrincipalFromCollateralRepayment,
        fullRepayWithCollateralQuoteError,
        validHealthRepayQuote
    } = useManageLeverageContext();

    const [collateralUiAmount, setCollateralUiAmount] = useState<number | undefined>();
    const [hasPendingUpdate, setHasPendingUpdate] = useState(false);
    const { isClosed: partialDisabledMarketHours } = useLoanMarketHoursAlertConfig(loanExpanded);
    const collateral = getZcLoanCollateral(loanExpanded);
    const ledger = getZcLedger(loanExpanded);

    const totalPrincipalDue = ledger?.ledgerDebt.total;
    const totalDueInCollateral = fullRepayAmountInCollateral;

    const effectiveRepaymentAmount = form.isFullRepay
        ? totalPrincipalDue
        : form.repayWithCollateralAmount
        ? customRepayAmountInPrincipalFromCollateralRepayment
        : 0;

    const debouncedFormUpdate = useMemo(
        () =>
            debounce((amount: number | undefined, isFullRepay: boolean) => {
                setForm((prev) => ({
                    ...prev,
                    repayWithCollateralAmount: amount,
                    isFullRepay
                }));
                setHasPendingUpdate(false);
            }, 500),
        [setForm]
    );

    function setMaxRepay() {
        debouncedFormUpdate.cancel();

        setForm((prev) => ({
            ...prev,
            isFullRepay: true,
            repayWithPrincipalAmount: ledger?.ledgerDebt.total,
            repayWithCollateralAmount: fullRepayAmountInCollateral
        }));

        setCollateralUiAmount(fullRepayAmountInCollateral);
        setHasPendingUpdate(false);
    }

    useEffect(() => {
        return () => debouncedFormUpdate.cancel();
    }, [debouncedFormUpdate]);

    const params = createRepaymentParams({
        principalPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.minOutputAmount,
            loanExpanded?.principalMetadata.decimals
        ),
        collateralPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.inputAmount,
            collateral?.metadata.decimals
        ),
        isFullRepay: form.isFullRepay,
        repayType: LoopWindType.Collateral,
        collateral,
        loanExpanded,
        quote: repayWithCollateralQuote
    });

    const { repay } = useRepay(params);

    const handleInputChange = useCallback(
        (amt: number | undefined) => {
            const isFullRepay = amt === totalDueInCollateral;
            setCollateralUiAmount(amt);
            setHasPendingUpdate(true);
            debouncedFormUpdate(amt, isFullRepay);
        },
        [totalDueInCollateral, debouncedFormUpdate]
    );

    const error = useMemo(() => {
        if (fullRepayWithCollateralQuoteError) return "Repay with collateral unavailable.";
        if (!validHealthRepayQuote) return "Invalid health for repay with collateral quote";
        if (repayWithCollateralQuoteError) {
            const errorMessage = getPandoraErrorMessage(
                repayWithCollateralQuoteError,
                "Collateral quote unavailable for input amount. Please try a different amount."
            );
            return errorMessage;
        }
        return null;
    }, [fullRepayWithCollateralQuoteError, repayWithCollateralQuoteError, validHealthRepayQuote]);

    const ctaError = useMemo(() => {
        const inputAmount = collateralUiAmount ?? form.repayWithCollateralAmount;
        if (fullRepayWithCollateralQuoteLoading || repayWithCollateralQuoteLoading) return "Loading";
        if (!inputAmount) return "Enter an amount";
        if (!form.isFullRepay && inputAmount > totalDueInCollateral) return "Exceeds total due";
        return null;
    }, [
        collateralUiAmount,
        form.repayWithCollateralAmount,
        form.isFullRepay,
        totalDueInCollateral,
        fullRepayWithCollateralQuoteLoading,
        repayWithCollateralQuoteLoading
    ]);

    const isLoading = repayWithCollateralQuoteLoading || hasPendingUpdate || fullRepayWithCollateralQuoteLoading;
    const isDisabledInput = fullRepayWithCollateralQuoteError || fullRepayWithCollateralQuoteLoading;

    return (
        <RepaymentComponent
            inputValue={collateralUiAmount}
            inputMetadata={collateral?.metadata}
            onInputChange={handleInputChange}
            disabledInput={isDisabledInput}
            totalDueInBalanceDisplay={totalDueInCollateral}
            topLevelTotalPrincipalDue={totalPrincipalDue}
            principalMetadata={loanExpanded?.principalMetadata}
            repaymentAmount={effectiveRepaymentAmount}
            loading={isLoading}
            balance={collateral?.amount ?? 0}
            balanceMetadata={collateral?.metadata}
            balanceLoading={fullRepayWithCollateralQuoteLoading}
            balanceError={fullRepayWithCollateralQuoteError}
            error={error}
            ctaError={ctaError}
            onRepay={repay}
            onSetMax={setMaxRepay}
            showMarketHours={!!partialDisabledMarketHours && !form.isFullRepay}
            loanExpanded={loanExpanded}
            repayType={LoopWindType.Collateral}
            repayLabel="Repay with"
        />
    );
}
